import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Col, Form, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { listSitesForPartner } from '../../../modules/sites/api';
import { listCollectionPointsForPartner } from '../../../modules/collection-points/api';

/**
 * Site (and collection-box) picker for the pickup / delivery edit forms.
 *
 * OVO-253 gave order CREATION a site-first flow, but editing an existing order
 * still offered only free-text name + address — so an operator could not point
 * an order at a catalog Site, and any correction silently detached it from the
 * catalog. This brings the same choice to the edit forms.
 *
 * Picking a site overwrites the name/address/coordinates below it (and the
 * phone when the site has one) and carries the FK, exactly as order creation
 * does. Leaving it empty keeps whatever is typed in those fields, so the
 * "different address" case still works.
 *
 * Boxes are pickup-only: a collection point is where goods are collected.
 */
export default function SitePickerFields({ form, side, partnerId, order }) {
  const { t } = useTranslation('orders');
  const isPickup = side === 'pickup';
  const siteField = isPickup ? 'pickupSiteId' : 'deliverySiteId';

  const [sites, setSites] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const selectedSiteId = Form.useWatch(siteField, form);

  useEffect(() => {
    if (!partnerId) {
      setSites([]);
      setBoxes([]);
      return;
    }
    listSitesForPartner(partnerId).then(({ response }) => {
      setSites(response?.items || response || []);
    });
    if (isPickup) {
      listCollectionPointsForPartner(partnerId).then(({ response }) => {
        setBoxes(response?.items || response || []);
      });
    }
  }, [partnerId, isPickup]);

  const siteBoxes = useMemo(
    () => boxes.filter((b) => b.siteId === selectedSiteId && !b.isArchived),
    [boxes, selectedSiteId],
  );

  // Exactly one box at this site ⇒ preselect it; none ⇒ clear the stale one.
  useEffect(() => {
    if (!isPickup) return;
    if (siteBoxes.length === 1) {
      form.setFieldsValue({ collectionPointId: siteBoxes[0].id });
    } else if (siteBoxes.length === 0) {
      form.setFieldsValue({ collectionPointId: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSiteId, siteBoxes.length]);

  const applySite = (siteId) => {
    const site = sites.find((s) => s.id === siteId);
    if (!site) return;
    // Mirror order-create: the catalog Site is the identity, so it overwrites
    // the snapshot fields the operator sees underneath.
    const patch = isPickup
      ? {
          pickupName: site.name,
          pickupAddress: { address: site.address, latLng: { lat: site.lat, lng: site.lng } },
          ...(site.phone ? { pickupPhone: site.phone } : {}),
        }
      : {
          deliveryName: site.name,
          deliveryAddress: { address: site.address, latLng: { lat: site.lat, lng: site.lng } },
          ...(site.phone ? { deliveryPhone: site.phone } : {}),
        };
    form.setFieldsValue(patch);
  };

  if (!partnerId) {
    return (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('form.siteNeedsPartner', 'This order has no partner, so there is no site catalog to choose from.')}
      />
    );
  }

  return (
    <Row gutter={24}>
      <Col span={isPickup ? 6 : 12}>
        <Form.Item
          name={siteField}
          label={isPickup ? t('form.pickupSite', 'Pickup site') : t('form.deliverySite', 'Delivery site')}
          extra={t('form.siteOverwritesHint', 'Selecting a site fills in the name, address and coordinates below.')}
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('form.siteSelectPlaceholder', 'Search a site…')}
            onChange={applySite}
            options={sites.map((s) => ({ value: s.id, label: `${s.name} — ${s.address}` }))}
          />
        </Form.Item>
      </Col>
      {isPickup && (
        <Col span={6}>
          <Form.Item
            name="collectionPointId"
            label={t('form.collectionPoint', 'Collection box')}
            extra={
              selectedSiteId && siteBoxes.length === 0 ? t('form.noBoxAtSite', 'This site has no box.') : undefined
            }
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              disabled={!selectedSiteId || siteBoxes.length === 0}
              placeholder={t('form.boxSelectPlaceholder', 'Box…')}
              options={siteBoxes.map((b) => ({
                value: b.id,
                label: b.externalRef ? `${b.name} (${b.externalRef})` : b.name,
              }))}
            />
          </Form.Item>
        </Col>
      )}
      {order?.[isPickup ? 'pickupSite' : 'deliverySite'] && !selectedSiteId && (
        <Col span={12}>
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 30 }}
            message={t('form.siteClearedWarning', 'Clearing the site detaches this order from the catalog.')}
          />
        </Col>
      )}
    </Row>
  );
}
